/**
 * DIPS OAuth リダイレクトページ - Retro-Brutalistic デザインシステム
 *
 * DIPS2.0認証後のコールバックを処理し、
 * 認可コードをアクセストークンに交換する
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dipsApi } from '../services/api';

// ===== Retro-Brutalistic UI コンポーネント =====

function Spinner() {
  return (
    <svg className="animate-spin w-10 h-10 text-[#0068b7]" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function QuestWindow({ children }) {
  return (
    <div
      className="relative bg-white border-4 border-[#0068B7] rounded-xl p-8"
      style={{ boxShadow: '0 8px 0 0 #00508e' }}
    >
      <div className="absolute inset-2 border-2 border-[#0068B7]/20 rounded-lg pointer-events-none" />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function AeroButton({ onClick, children, variant = 'secondary' }) {
  const styles = {
    primary: {
      className: 'text-white bg-[#0068B7] hover:bg-[#0057a0] active:translate-y-0.5',
      shadow: '0 4px 0 0 #00508e',
    },
    secondary: {
      className: 'text-[#0068B7] bg-white border-2 border-[#0068B7] hover:bg-blue-50 active:translate-y-0.5',
      shadow: '0 4px 0 0 #0068B740',
    },
  };
  const s = styles[variant] || styles.secondary;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${s.className}`}
      style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, boxShadow: s.shadow }}
    >
      {children}
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <span
      style={{
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#717782',
      }}
    >
      {children}
    </span>
  );
}

// ===== メインコンポーネント =====

export default function DipsRedirect() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [error, setError] = useState(null);
  const [apiType, setApiType] = useState('aircraft');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setError(`DIPS認証エラー: ${searchParams.get('error_description') || errorParam}`);
      return;
    }

    if (!code) {
      setStatus('error');
      setError('認可コードが見つかりません');
      return;
    }

    // stateからapiTypeを抽出（形式: "apiType:uuid"）
    let detectedApiType = 'aircraft';
    if (state && state.includes(':')) {
      detectedApiType = state.split(':')[0];
    }
    setApiType(detectedApiType);

    // トークン交換を実行
    const exchangeToken = async () => {
      try {
        const result = await dipsApi.exchangeToken(code, detectedApiType);
        setStatus('success');

        if (window.opener) {
          window.opener.postMessage({
            type: 'DIPS_AUTH_SUCCESS',
            apiType: detectedApiType,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            expiresIn: result.expiresIn,
          }, '*');
        }

        // 1秒後に自動的にウィンドウを閉じる
        setTimeout(() => window.close(), 1000);
      } catch (err) {
        setStatus('error');
        setError(err.message);

        if (window.opener) {
          window.opener.postMessage({
            type: 'DIPS_AUTH_ERROR',
            error: err.message,
          }, '*');
        }
      }
    };

    exchangeToken();
  }, [searchParams]);

  const isFlightPlan = apiType === 'flightPlan';

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'linear-gradient(to bottom, #dceeff, #f8f9ff)' }}
    >
      <div className="max-w-md w-full">
        <QuestWindow>
          {/* ページタイトル */}
          <div className="text-center mb-8">
            <h1
              className="text-xl text-[#0068B7]"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
            >
              DIPS2.0 認証
            </h1>
            <SectionLabel>{isFlightPlan ? 'Flight Plan · FPR' : 'Aircraft Registry · DRS'}</SectionLabel>
          </div>

          {/* 処理中 */}
          {status === 'processing' && (
            <div className="flex flex-col items-center text-center gap-5">
              <Spinner />
              <div>
                <p
                  className="text-[#0068B7]"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600 }}
                >
                  認証処理中...
                </p>
                <p className="text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                  トークンを取得しています
                </p>
              </div>
            </div>
          )}

          {/* 成功 */}
          {status === 'success' && (
            <div className="flex flex-col items-center text-center gap-5">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#0068b7', boxShadow: '0 5px 0 0 #00508e' }}
              >
                <span
                  className="material-symbols-outlined text-white"
                  style={{ fontSize: '32px', fontVariationSettings: "'FILL' 1" }}
                >
                  check_circle
                </span>
              </div>
              <div>
                <h2
                  className="text-lg text-[#0068B7]"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
                >
                  認証完了
                </h2>
                <div className="h-0.5 w-12 mx-auto my-2 border-b-2 border-[#0068B7]/40" />
                <p className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#414751', lineHeight: '1.6' }}>
                  DIPS2.0との連携が完了しました。<br />
                  このウィンドウは自動的に閉じます。
                </p>
              </div>
              <AeroButton onClick={() => window.close()}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                閉じる
              </AeroButton>
            </div>
          )}

          {/* エラー */}
          {status === 'error' && (
            <div className="flex flex-col items-center text-center gap-5">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#DC2626', boxShadow: '0 5px 0 0 #991B1B' }}
              >
                <span
                  className="material-symbols-outlined text-white"
                  style={{ fontSize: '32px', fontVariationSettings: "'FILL' 1" }}
                >
                  error
                </span>
              </div>
              <div>
                <h2
                  className="text-lg"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#DC2626' }}
                >
                  認証エラー
                </h2>
                <div className="h-0.5 w-12 mx-auto my-2 border-b-2" style={{ borderColor: 'rgba(220,38,38,0.4)' }} />
              </div>
              <div
                className="w-full flex items-start gap-2 px-4 py-3 rounded-lg border-2 text-left"
                style={{ backgroundColor: '#FEF2F2', borderColor: '#DC2626', color: '#991B1B' }}
              >
                <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '18px', marginTop: '1px', fontVariationSettings: "'FILL' 1" }}>error</span>
                <p className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{error}</p>
              </div>
              <AeroButton onClick={() => window.close()}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                閉じる
              </AeroButton>
            </div>
          )}
        </QuestWindow>
      </div>
    </div>
  );
}
